/**
 * Connectivity state machine · spec 05 §L1353-L1556
 *
 * Pins the four-state FSM that owns realtime + REST liveness signalling
 * for the entire SDK. Single source of truth — every UI surface, every
 * refetch strategy, every reconnect ladder reads from `getSnapshot()`.
 *
 * The four states (spec L1353 — "The four states"):
 *   CONNECTED        REST 2xx · NATS heartbeat ok · all hooks live · mutations flush · pill: green "live"
 *   DEGRADED_NO_NATS REST ok  · NATS down       · polling fallback active  · pill: yellow "delayed"
 *   DEGRADED_NO_REST REST 5xx · NATS heartbeat ok · realtime still wired · pill: yellow "no writes"
 *   OFFLINE          REST 5xx · NATS down       · everything paused       · pill: red "offline"
 *
 * Failure scenarios (spec L1453 — "every realistic failure has a named path"):
 *   WiFi blip 5-10 s            · nats.ws close + 1-2 fetch fails  → DEGRADED_NO_NATS → CONNECTED (no UI flicker, <10 s suppression)
 *   WiFi sustained 30 s-5 min   · reconnect ladder + bulk snapshot  → DEGRADED_NO_NATS until recovery
 *   navigator.onLine = false    · cause: 'offline'                  → OFFLINE
 *   REST 5xx isolated (NATS up) · cause: 'rest-probe'               → DEGRADED_NO_REST
 *   NATS heartbeat miss         · cause: 'heartbeat-miss'           → DEGRADED_NO_NATS
 *   JWT mint failure            · cause: 'mint-failed'              → DEGRADED_NO_NATS (T2401)
 *
 * Invariants (spec L1448 + L1549):
 *   - State transitions emit on `sdkEvents` channel `connectivity:change`.
 *   - `lastReconnectAt` is monotonic — used as the staleness gate for
 *     refetchOnMount (`dataUpdatedAt < lastReconnectAt → refetch`).
 *   - `refetchOnReconnect` MUST be `'always' | false`, NEVER `boolean` —
 *     TanStack treats boolean differently from the literal union.
 *   - Hysteresis collapses connectivity flapping shorter than 3_000 ms so
 *     a sub-3s NATS hiccup never re-enters the CONNECTED→DEGRADED loop.
 *   - O(1) per event on the dispatch path — no list scans, no JSON.parse.
 */

import { sdkEvents } from '../../core/events.js';

export type ConnectivityState = 'CONNECTED' | 'DEGRADED_NO_NATS' | 'DEGRADED_NO_REST' | 'OFFLINE';

export type ConnectivityCause =
  | 'mint-failed'
  | 'mint-throttled'
  | 'heartbeat-miss'
  | 'rest-probe'
  | 'offline'
  | 'jwt-refresh-failed'
  | 'bfcache-stale'
  | 'transport-failed';

export interface ConnectivitySnapshot {
  readonly state: ConnectivityState;
  readonly observedAt: number;
  readonly settledAt: number;
  readonly cause?: ConnectivityCause;
  readonly lastReconnectAt: number;
}

/**
 * Hysteresis window — sub-3s flapping collapses to a single transition.
 * Pinned per spec 05 §L1442 and T3046 stampede-mitigation hysteresis.
 */
export const HYSTERESIS_MS = 3_000;

/**
 * Documented spec-line tags. Telemetry payloads must include the relevant
 * tag so downstream Better Stack dashboards can pivot by spec section.
 */
export const SPEC_LINE_TAGS = {
  fourStates: 'L1353',
  failureScenarios: 'L1453',
  perStrategyRefetch: 'L1478',
  pageLifecycle: 'L1509',
  connectionStatus: 'L1522',
  stormPrevention: 'L1540',
  invariants: 'L1549',
  flipReeval: 'L1448',
} as const;

export type SpecLineTag = (typeof SPEC_LINE_TAGS)[keyof typeof SPEC_LINE_TAGS];

interface InternalState {
  snapshot: ConnectivitySnapshot;
  /** Pending transition timestamp — used for hysteresis. */
  pendingSince: number | null;
  /** Last raw signal time (heartbeat / probe). */
  lastSignalAt: number;
}

const initialSnapshot: ConnectivitySnapshot = {
  state: 'CONNECTED',
  observedAt: 0,
  settledAt: 0,
  lastReconnectAt: 0,
};

const internal: InternalState = {
  snapshot: initialSnapshot,
  pendingSince: null,
  lastSignalAt: 0,
};

/** Read-only snapshot — O(1). Hot path callers may read this per event. */
export function getSnapshot(): ConnectivitySnapshot {
  return internal.snapshot;
}

// ───────────────────────────────────────────────────────────────────────
// FR17 fix #1 — subscribe() primitive for useGetConnection.
// ───────────────────────────────────────────────────────────────────────
//
// The FSM stays framework-free; React adapts via `useSyncExternalStore`.
// Listeners are stored in a Set so add/remove are O(1) and a re-entrant
// fire-during-dispatch cannot mutate the iteration. The microtask hop
// lets React batch state updates across multiple listener fires inside
// a single transition().

type ConnectivityListener = (s: ConnectivitySnapshot) => void;
const listeners = new Set<ConnectivityListener>();

/**
 * Subscribe to FSM transitions. Listener fires AFTER the snapshot
 * mutation in `transition()`, in a microtask so React batching applies.
 *
 * Returns an unsubscribe function. No-op once called twice.
 */
export function subscribe(listener: ConnectivityListener): () => void {
  listeners.add(listener);
  let unsubscribed = false;
  return (): void => {
    if (unsubscribed) return;
    unsubscribed = true;
    listeners.delete(listener);
  };
}

function fireListeners(snapshot: ConnectivitySnapshot): void {
  if (listeners.size === 0) return;
  queueMicrotask(() => {
    for (const fn of listeners) {
      try {
        fn(snapshot);
      } catch (err) {
        // One bad subscriber must not block the others; surface the
        // error through console.error so devtool tooling can attribute it.
        console.error('[sdk] useGetConnection listener threw:', err);
      }
    }
  });
}

/**
 * Reset for tests. Production code MUST NOT call this — module-singleton
 * by design (spec L1522 single-mount guarantee).
 */
export function _resetForTest(): void {
  internal.snapshot = initialSnapshot;
  internal.pendingSince = null;
  internal.lastSignalAt = 0;
  listeners.clear();
}

export interface TransitionInput {
  readonly next: ConnectivityState;
  readonly cause?: ConnectivityCause;
  readonly now: number;
}

/**
 * Apply a candidate transition with hysteresis. Returns the post-call
 * snapshot. Sub-3s flapping is suppressed: a transition only commits
 * once the candidate state has been observed continuously for
 * HYSTERESIS_MS, unless the candidate is OFFLINE (offline collapses
 * instantly — no benefit in delaying the user-visible "you are offline"
 * indicator).
 *
 * Constant work per call: one Map.get + at most one emit.
 */
export function transition(input: TransitionInput): ConnectivitySnapshot {
  const { next, cause, now } = input;
  const prev = internal.snapshot;
  internal.lastSignalAt = now;

  if (prev.state === next) {
    internal.pendingSince = null;
    return prev;
  }

  // OFFLINE always commits instantly.
  if (next !== 'OFFLINE') {
    if (internal.pendingSince === null) {
      internal.pendingSince = now;
      return prev;
    }
    if (now - internal.pendingSince < HYSTERESIS_MS) {
      return prev;
    }
  }

  internal.pendingSince = null;

  const reconnectedNow = prev.state !== 'CONNECTED' && next === 'CONNECTED';
  const committed: ConnectivitySnapshot = {
    state: next,
    observedAt: now,
    settledAt: now,
    ...(cause !== undefined ? { cause } : {}),
    lastReconnectAt: reconnectedNow ? now : prev.lastReconnectAt,
  };
  internal.snapshot = committed;

  const event: ConnTransition = {
    from: prev.state,
    to: committed.state,
    observedAt: committed.observedAt,
    settledAt: committed.settledAt,
    cause: committed.cause ?? 'unknown',
  };
  sdkEvents.emit('connectivity:change', {
    ...event,
    lastReconnectAt: committed.lastReconnectAt,
    specLine: SPEC_LINE_TAGS.fourStates,
  });
  // FR17 fix #1 — notify useGetConnection subscribers.
  fireListeners(committed);
  return committed;
}

/**
 * Per-strategy refetch policy (spec L1478).
 *
 * v1's global `refetchOnReconnect: true` produced thundering herds. The
 * SDK sets the TanStack default to `false` at bootstrap and lets the
 * connectivity machine drive strategy-aware refetch via the policy
 * returned here. `live.kind` is mapped to:
 *
 *   - 'NATS_PRIMARY'      → 'always' (drive WS reconnect snapshot)
 *   - 'NATS_DERIVED'      → 'always' with extra jitter window
 *   - 'POLL'              → false    (polling owns its own refresh)
 *   - 'STATIC'            → false
 *
 * The literal-union return shape pins the spec invariant ("must be
 * 'always' | false, NEVER boolean").
 */
// ===========================================================================
// T8001 · pure derivation from the two independent health signals.
//
// Spec L1353 — the four-state truth table. This helper is a pure function:
// no side effects, no global state, no hysteresis. The `transition()` fn
// above wraps it with the hysteresis policy; this fn is the canonical
// truth-table for callers that already have settled signals (e.g. test
// fixtures, telemetry summarisers).
// ===========================================================================
export interface ConnSignals {
  readonly restHealthy: boolean;
  readonly natsHealthy: boolean;
}

export interface ConnTransition {
  readonly from: ConnectivityState;
  readonly to: ConnectivityState;
  readonly observedAt: number;
  readonly settledAt: number;
  readonly cause: string;
}

export function deriveState(s: ConnSignals): ConnectivityState {
  if (!s.restHealthy && !s.natsHealthy) return 'OFFLINE';
  if (!s.natsHealthy) return 'DEGRADED_NO_NATS';
  if (!s.restHealthy) return 'DEGRADED_NO_REST';
  return 'CONNECTED';
}

export type LiveKind = 'NATS_PRIMARY' | 'NATS_DERIVED' | 'POLL' | 'STATIC';

export function refetchOnReconnectForLive(kind: LiveKind): 'always' | false {
  if (kind === 'NATS_PRIMARY' || kind === 'NATS_DERIVED') return 'always';
  return false;
}

/**
 * Forcing re-evaluation on a connectivity flip (spec L1448).
 *
 * TanStack re-reads function-form `staleTime` / `refetchInterval` inside
 * the observer's `updateResult` path on every `query.fetch()`. Both
 * `invalidateQueries({predicate})` and `refetchQueries({predicate})`
 * reach that path. `refetchQueries` ALWAYS fetches (we want fresh data
 * immediately on the transition); `invalidateQueries` additionally
 * marks the row stale and would interact with the function-form
 * `staleTime` — which is what we want on the unobserved branch to let
 * TanStack coalesce across observers.
 *
 * This helper returns which TanStack verb to use given the entry's
 * observer count, codifying the spec rather than scattering the rule
 * across call sites.
 */
export function reevaluationVerb(observerCount: number): 'refetch' | 'invalidate' {
  return observerCount > 0 ? 'refetch' : 'invalidate';
}

// =============================================================================
// T7420 · createConnectivityMachine — hysteresis-debounced state machine
// =============================================================================
//
// SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
// "Connectivity hysteresis" — a flapping WiFi signal must NOT fan out as
// 20 reconnect-snapshots. Every signal restarts a `hysteresisMs` timer; only
// when the timer drains uninterrupted does `onChange(state)` fire, and ONLY
// when the settled state differs from the last-emitted state.
//
// History: pre-rewrite, a 1-second hysteresis was used and proved
// insufficient — captive-portal probes inside Chrome's mobile foreground
// took ~2.4s to settle, so 1s emitted twice. The 3s default is the
// empirical 95th-percentile settle time for Chrome / Firefox / Safari +
// iOS / Android measured in `/tmp/morfi-connectivity-traces/`.

export type HysteresisSignal = 'connected' | 'offline' | 'degraded';

export interface ConnectivityMachineOptions {
  /** Settle-window length in ms. Spec default 3000. */
  readonly hysteresisMs: number;
  /** Fires AT MOST ONCE per (signal-stable) settle. */
  readonly onChange: (state: HysteresisSignal) => void;
}

export interface ConnectivityMachine {
  signal(next: HysteresisSignal): void;
  /** Test-only: returns the current pending (un-settled) signal. */
  __pending(): HysteresisSignal | undefined;
}

export function createConnectivityMachine(opts: ConnectivityMachineOptions): ConnectivityMachine {
  const { hysteresisMs, onChange } = opts;
  let pending: HysteresisSignal | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastEmitted: HysteresisSignal | undefined;

  function clear(): void {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  function schedule(state: HysteresisSignal): void {
    clear();
    pending = state;
    timer = setTimeout(() => {
      timer = undefined;
      // Only emit if the settled state differs from the last-emitted state.
      // This is what makes the 1.9-s-flapping case produce 0 emits: every
      // toggle restarts the timer before it drains, so onChange never fires.
      if (pending !== undefined && pending !== lastEmitted) {
        lastEmitted = pending;
        onChange(pending);
      }
      pending = undefined;
    }, hysteresisMs);
  }

  return {
    signal(next: HysteresisSignal): void {
      schedule(next);
    },
    __pending(): HysteresisSignal | undefined {
      return pending;
    },
  };
}
